import React, { useState } from 'react';
import { Eye, X, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ui/ToastProvider';
import styles from './ViewAsBar.module.css';

/**
 * Seeing the app as another role.
 *
 * Two pieces in one file because they are two faces of the same control: the
 * picker, which only a real admin sees, and the banner, which replaces it while
 * a preview is running.
 *
 * The banner is deliberately hard to miss and always offers the way out — a
 * preview you forget you are in is a support ticket about missing features.
 */

const ROLES = [
  { id: 'manager', label: 'Manager' },
  { id: 'marketer', label: 'Marketer' },
  { id: 'chatter', label: 'Chatter' },
  { id: 'creator', label: 'Creator' },
];

function ViewAsBar() {
  const { user, startViewAs, stopViewAs } = useAuth();
  const { show: showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const previewing = !!user?.viewAs;
  // Only a real admin gets the picker. While previewing, `role` is the role
  // being previewed, so the check has to look at what the token says it really
  // is — otherwise the control would vanish the moment you used it.
  const isRealAdmin = previewing || user?.permissions?.includes('manage_users');

  if (!user || !isRealAdmin) return null;

  const enter = async (role) => {
    setOpen(false);
    setBusy(true);
    try {
      await startViewAs(role);
      // A full reload rather than a re-render: every page holds data fetched
      // with the old token and its own idea of what this role may do, and
      // half-swapped state is worse than a one-second wait.
      window.location.assign('/');
    } catch (err) {
      showToast({ variant: 'danger', message: err.message });
      setBusy(false);
    }
  };

  const exit = async () => {
    setBusy(true);
    try {
      await stopViewAs();
      window.location.assign('/');
    } catch (err) {
      showToast({ variant: 'danger', message: err.message });
      setBusy(false);
    }
  };

  if (previewing) {
    return (
      <div className={styles.banner} role="status">
        <Eye size={15} />
        <span className={styles.bannerText}>
          Viewing as <strong>{user.role}</strong> — this is your own account with that role&rsquo;s
          permissions, so anything you do here is real.
        </span>
        <button type="button" className={styles.exit} onClick={exit} disabled={busy}>
          <X size={14} /> Back to admin
        </button>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        title="See the app as another role"
      >
        <Eye size={15} />
        <span className={styles.triggerLabel}>View as</span>
        <ChevronDown size={13} />
      </button>

      {open && (
        <>
          <button type="button" className={styles.scrim} onClick={() => setOpen(false)} aria-label="Close" />
          <div className={styles.menu} role="menu">
            <p className={styles.menuNote}>
              You, with that role&rsquo;s permissions. Lasts 30 minutes.
            </p>
            {ROLES.map((r) => (
              <button key={r.id} type="button" onClick={() => enter(r.id)}>{r.label}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default ViewAsBar;
